/**
 * Ops map viewport — measure chrome, set MapLibre padding, resize canvas.
 * Soft≠Live: layout only. Keeps fence framing honest across browsers / DPR.
 */

/**
 * @param {DOMRectReadOnly | {width:number,height:number,left:number,top:number,right:number,bottom:number}} a
 * @param {DOMRectReadOnly | {width:number,height:number,left:number,top:number,right:number,bottom:number}} b
 */
function intersects(a, b) {
  return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
}

/**
 * MapLibre padding from live panel geometry (side dock vs bottom sheet).
 * Hard-coded right:420 crushed netbooks and left the fence "locked".
 *
 * @param {Element | null | undefined} host  .map-host
 * @returns {{ top:number, right:number, bottom:number, left:number }}
 */
export function measureOpsMapPadding(host) {
  const pad = { top: 88, right: 72, bottom: 96, left: 72 };
  if (!host || typeof host.getBoundingClientRect !== "function") return pad;
  let hostBox;
  try {
    hostBox = host.getBoundingClientRect();
  } catch {
    return pad;
  }
  const w = Math.max(0, Number(hostBox.width) || 0);
  const h = Math.max(0, Number(hostBox.height) || 0);
  if (w < 40 || h < 40) return pad;

  pad.top = Math.max(72, Math.min(120, Math.round(h * 0.11)));
  pad.left = Math.max(52, Math.min(96, Math.round(w * 0.06)));
  pad.bottom = Math.max(64, Math.min(132, Math.round(h * 0.1)));
  pad.right = Math.max(52, Math.min(96, Math.round(w * 0.07)));

  let panel = null;
  try {
    panel =
      document.querySelector('.panel[data-cmc-panel="ops-side"]') ||
      document.querySelector(".panel.panel--ops-rail") ||
      document.querySelector(".panel--ops");
  } catch {
    panel = null;
  }
  if (panel && typeof panel.getBoundingClientRect === "function") {
    let pb;
    try {
      pb = panel.getBoundingClientRect();
    } catch {
      pb = null;
    }
    if (pb && pb.width > 24 && pb.height > 24 && intersects(hostBox, pb)) {
      const sideDock =
        pb.height >= h * 0.48 && pb.left > hostBox.left + w * 0.32;
      const bottomDock =
        pb.width >= w * 0.55 && pb.top > hostBox.top + h * 0.38;
      if (sideDock) {
        const gap = Math.max(0, hostBox.right - pb.left);
        pad.right = Math.min(Math.round(w * 0.58), Math.ceil(gap + 14));
      } else if (bottomDock) {
        const gap = Math.max(0, hostBox.bottom - pb.top);
        pad.bottom = Math.min(Math.round(h * 0.58), Math.ceil(gap + 14));
        pad.right = Math.max(pad.right, 48);
      } else {
        const rightGap = Math.max(0, hostBox.right - pb.left);
        const bottomGap = Math.max(0, hostBox.bottom - pb.top);
        if (rightGap > bottomGap && rightGap > 60) {
          pad.right = Math.min(Math.round(w * 0.52), Math.ceil(rightGap + 12));
        } else if (bottomGap > 60) {
          pad.bottom = Math.min(Math.round(h * 0.52), Math.ceil(bottomGap + 12));
        }
      }
    }
  }

  /* Keep enough free canvas for drag / fence measurement. */
  const minFreeW = Math.min(220, Math.max(140, w * 0.28));
  const minFreeH = Math.min(180, Math.max(120, h * 0.28));
  let freeW = w - pad.left - pad.right;
  let freeH = h - pad.top - pad.bottom;
  if (freeW < minFreeW) {
    pad.right = Math.max(28, pad.right - (minFreeW - freeW));
  }
  if (freeH < minFreeH) {
    pad.bottom = Math.max(28, pad.bottom - (minFreeH - freeH));
  }
  return {
    top: Math.round(pad.top),
    right: Math.round(pad.right),
    bottom: Math.round(pad.bottom),
    left: Math.round(pad.left),
  };
}

/**
 * @param {*} map maplibregl.Map
 * @param {Element | null | undefined} host
 * @param {{ view?: string }} [opts]
 * @returns {{ top:number, right:number, bottom:number, left:number } | null}
 */
export function syncOpsMapViewport(map, host, opts = {}) {
  if (!map || !host) return null;
  try {
    if (typeof map.resize === "function") map.resize();
  } catch {
    /* */
  }
  const pad = measureOpsMapPadding(host);
  const view = String(opts.view || "site");
  try {
    if (view === "globe") {
      let w = 800;
      try {
        w = host.getBoundingClientRect().width || w;
      } catch {
        /* */
      }
      map.setPadding({
        top: Math.max(8, Math.round(pad.top * 0.15)),
        right: Math.min(pad.right, Math.round(w * 0.26)),
        bottom: Math.max(40, Math.round(pad.bottom * 0.7)),
        left: Math.max(24, Math.round(pad.left * 0.75)),
      });
    } else {
      map.setPadding(pad);
    }
  } catch {
    /* */
  }
  return pad;
}

/**
 * Visual / WebGL floor for cross-browser MapLibre.
 * @param {Element | null | undefined} host
 * @returns {{ ok:boolean, reason?:string }}
 */
export function opsMapContainerOk(host) {
  if (!host || typeof host.getBoundingClientRect !== "function") {
    return { ok: false, reason: "no-host" };
  }
  let box;
  try {
    box = host.getBoundingClientRect();
  } catch {
    return { ok: false, reason: "rect-fail" };
  }
  if ((Number(box.width) || 0) < 32 || (Number(box.height) || 0) < 32) {
    return { ok: false, reason: "zero-size" };
  }
  return { ok: true };
}
